import { useEffect, useState } from "react";
import BodyHeader from "../../../components/common/CommonBodyHeader";
import CustomTable from "../../../components/common/CustomTable";
import { useDispatch, useSelector } from "react-redux";
import { backdropLoadingAction } from "../../../redux/features/helperSlice";
import toast from "react-hot-toast";
import * as XLSX from "xlsx";
import { useBoolean } from "../../../Hooks/useBoolean";
import DownloadReportButton from "../../../components/common/DownloadReportButton";
import CustomInput from "../../../components/common/CustomInput/CustomInput";
import CustomDropdown from "../../../components/common/CustomDropdown/CustomDropdown";
import { reportDropDownRecordAsync } from "../../../redux/features/report-master/reportDropDownRecordSlice";
import { useFormik } from "formik";
import moment from "moment";
import { fleetEMIReportAsync } from "../../../redux/features/reportSlice";

export default function EmiReport() {
   const dispatch = useDispatch();
   const [records, setRecords] = useState([]);

   const dropDownMaster = useSelector((state) => state.reportDropDownRecordSlice);

   const companyData = [
      { label: "Select..", value: "" },
      ...(dropDownMaster?.data?.company_list?.map?.((item) => ({
         label: item?.company_name,
         value: item?.id,
      })) || []),
   ];

   const fleetData = [
      { label: "Select..", value: "" },
      ...(dropDownMaster?.data?.vehicle_list?.map?.((item) => ({
         label: item?.vehicle_no,
         value: item?.id,
      })) || []),
   ];

   const formik = useFormik({
      initialValues: {
         company: "",
         fleet: "",
         from_date: moment().startOf("month").format("YYYY-MM-DD"),
         to_date: moment().endOf("month").format("YYYY-MM-DD"),
      },
   });

   const { values, getFieldProps, setFieldValue, resetForm } = formik;

   const reportPopup = useBoolean();

   const columns = [
      {
         name: "Vehicle No.",
         selector: (row) => row?.vehicle_no,
         sortable: true,
      },
      {
         name: "Company Name",
         selector: (row) => row?.company?.company_name ?? row?.company_name ?? "-",
         sortable: true,
      },
      {
         name: "EMI Amount",
         selector: (row) => row?.emi_amount ?? "-",
         sortable: true,
      },
      {
         name: "EMI Start Date",
         selector: (row) =>
            row?.emi_start_date ? moment(row.emi_start_date).format("DD-MM-YYYY") : "-",
         sortable: true,
      },
      {
         name: "EMI End Date",
         selector: (row) =>
            row?.emi_end_date ? moment(row.emi_end_date).format("DD-MM-YYYY") : "-",
         sortable: true,
      },
      {
         name: "Total Loan Amount",
         selector: (row) => row?.total_emi_amount ?? "-",
         sortable: true,
      },
      {
         name: "Financed By",
         selector: (row) => row?.financed_by ?? "-",
         sortable: true,
      },
      {
         name: "EMI Date",

         selector: (row) => {
            if (!row?.emi_monthly_obligation_date) return "-";

            const emiDate = moment()
               .date(row.emi_monthly_obligation_date)
               .format("DD-MM-YYYY");

            return emiDate;
         },

         sortable: true,
      },
   ];

   const filteredRecords = records.filter((row) => {
      let matchCompany = true;
      let matchFleet = true;

      if (values.company) {
         const selectedCompany = dropDownMaster?.data?.company_list?.find(
            (c) => String(c.id) === String(values.company),
         );
         matchCompany =
            String(row?.company_id) === String(values.company) ||
            String(row?.company?.id) === String(values.company) ||
            (selectedCompany &&
               row?.company?.company_name === selectedCompany.company_name) ||
            (selectedCompany && row?.company_name === selectedCompany.company_name);
      }

      if (values.fleet) {
         const selectedFleet = dropDownMaster?.data?.vehicle_list?.find(
            (v) => String(v.id) === String(values.fleet),
         );
         matchFleet =
            String(row?.vehicle_id) === String(values.fleet) ||
            String(row?.id) === String(values.fleet) ||
            String(row?.vehicle?.id) === String(values.fleet) ||
            (selectedFleet && row?.vehicle_no === selectedFleet.vehicle_no);
      }

      return matchCompany && matchFleet;
   });

   useEffect(() => {
      dispatch(backdropLoadingAction(true));
      const filter_params = {
         model_id: 11,
         action_id: 3,
      };
      if (values.company) filter_params.company_id = values.company;
      if (values.fleet) filter_params.vehicle_id = values.fleet;
      if (values.from_date) {
         filter_params.from_date = values.from_date;
         filter_params.startDate = values.from_date;
      }
      if (values.to_date) {
         filter_params.to_date = values.to_date;
         filter_params.endDate = values.to_date;
      }

      dispatch(
         fleetEMIReportAsync({
            data: filter_params,
            done: (report) => {
               dispatch(backdropLoadingAction(false));
               if (report) {
                  const transformed = report.flat().filter(Boolean);
                  transformed.sort((a, b) => {
                     let comp = (a?.company?.company_name ?? "").localeCompare(
                        b?.company?.company_name ?? "",
                     );
                     if (comp !== 0) return comp;
                     return (a?.vehicle_no ?? "").localeCompare(b?.vehicle_no ?? "");
                  });
                  setRecords(transformed);
               } else {
                  setRecords([]);
               }
            },
            err: (error) => {
               dispatch(backdropLoadingAction(false));
               setRecords([]);
            },
         }),
      );
   }, [values.company, values.fleet, values.from_date, values.to_date]);

   useEffect(() => {
      dispatch(backdropLoadingAction(true));
      dispatch(
         reportDropDownRecordAsync({
            done: () => {
               dispatch(backdropLoadingAction(false));
            },
            err: (error) => {
               dispatch(backdropLoadingAction(false));
            },
         }),
      );
   }, []);

   const handleDownloadReport = async (data) => {
      dispatch(backdropLoadingAction(true));

      const filter_params = {
         model_id: 11,
         action_id: 3,
         ...data,
      };
      if (values.company) filter_params.company_id = values.company;
      if (values.fleet) filter_params.vehicle_id = values.fleet;
      if (values.from_date && !filter_params.from_date) {
         filter_params.from_date = values.from_date;
         filter_params.startDate = values.from_date;
      }
      if (values.to_date && !filter_params.to_date) {
         filter_params.to_date = values.to_date;
         filter_params.endDate = values.to_date;
      }

      dispatch(
         fleetEMIReportAsync({
            data: filter_params,
            err: () => {
               dispatch(backdropLoadingAction(false));
            },
            done: (report) => {
               const headers = [
                  "S. No.",
                  "Vehicle No.",
                  "Company Name",
                  "EMI Amount",
                  "EMI Start Date",
                  "EMI End Date",
                  "Total Loan Amount",
                  "Financed By",
                  "EMI Obligation Day",
               ];

               const emiArr = [];
               let item_count = 1;

               report?.flat()?.forEach?.((record, index) => {
                  if (record) {
                     let matchCompany = true;
                     let matchFleet = true;

                     if (values.company) {
                        const selectedCompany = dropDownMaster?.data?.company_list?.find(
                           (c) => String(c.id) === String(values.company),
                        );
                        matchCompany =
                           String(record?.company_id) === String(values.company) ||
                           String(record?.company?.id) === String(values.company) ||
                           (selectedCompany &&
                              record?.company?.company_name ===
                                 selectedCompany.company_name) ||
                           (selectedCompany &&
                              record?.company_name === selectedCompany.company_name);
                     }

                     if (values.fleet) {
                        const selectedFleet = dropDownMaster?.data?.vehicle_list?.find(
                           (v) => String(v.id) === String(values.fleet),
                        );
                        matchFleet =
                           String(record?.vehicle_id) === String(values.fleet) ||
                           String(record?.id) === String(values.fleet) ||
                           String(record?.vehicle?.id) === String(values.fleet) ||
                           (selectedFleet &&
                              record?.vehicle_no === selectedFleet.vehicle_no);
                     }

                     if (!matchCompany || !matchFleet) return;

                     const arr = [];
                     arr.push(
                        item_count,
                        record.vehicle_no ?? "-",
                        record.company?.company_name ?? "-",
                        record.emi_amount ?? "-",
                        record.emi_start_date ?? "-",
                        record.emi_end_date ?? "-",
                        record.total_emi_amount ?? "-",
                        record.financed_by ?? "-",
                        record.emi_monthly_obligation_date ?? "-",
                     );
                     item_count += 1;
                     emiArr.push(arr);
                  }
               });

               const sheetData = [headers, ...emiArr];

               const ws = XLSX.utils.aoa_to_sheet(sheetData);
               const wb = XLSX.utils.book_new();
               XLSX.utils.book_append_sheet(wb, ws, "Sheet1");
               XLSX.writeFile(wb, "FLEET_EMI_REPORT.xlsx");
               dispatch(backdropLoadingAction(false));
               reportPopup.onFalse();
            },
         }),
      );
   };

   return (
      <div>
         <BodyHeader title="Fleet EMI Report" />
         <div className="px-3">
            <div className="row align-items-center mb-3">
               <div className="col-lg-2">
                  <CustomDropdown
                     label="Vehicle Number"
                     optionData={fleetData}
                     value={
                        fleetData.find((option) => option.value === values.fleet) ||
                        fleetData[0]
                     }
                     onChange={(value) => {
                        const val = Array.isArray(value) ? value[0]?.value : value?.value;
                        setFieldValue("fleet", val || "");
                     }}
                  />
               </div>
               <div className="col-lg-2">
                  <CustomDropdown
                     label="Company"
                     optionData={companyData}
                     value={
                        companyData.find((option) => option.value === values.company) ||
                        companyData[0]
                     }
                     onChange={(value) => {
                        const val = Array.isArray(value) ? value[0]?.value : value?.value;
                        setFieldValue("company", val || "");
                     }}
                  />
               </div>
               <div className="col-lg-2">
                  <CustomInput
                     label="EMI Date From"
                     inputType="date"
                     placeholder="From"
                     {...getFieldProps("from_date")}
                  />
               </div>
               <div className="col-lg-2">
                  <CustomInput
                     label="EMI Date To"
                     inputType="date"
                     placeholder="To"
                     {...getFieldProps("to_date")}
                  />
               </div>
               <div className="col-lg-2">
                  <button
                     type="button"
                     className="btn btn-outline-secondary w-100 mt-4"
                     onClick={() => resetForm()}
                  >
                     Clear Filters
                  </button>
               </div>
               <div className="col-lg-2 pt-2 mt-2 mb-2 text-right">
                  <DownloadReportButton
                     title="Download Report"
                     onDownloadReport={handleDownloadReport}
                     confirmPopup={reportPopup}
                     modal_title="Select EMI Date"
                  />
               </div>
            </div>

            <CustomTable columns={columns} data={filteredRecords} />
         </div>
      </div>
   );
}
